'use client';

import { FeCarouselV2 } from '../../../components/FeCarousel';
import { RewardThumbnail } from '../../../components/RewardThumbnail';
import { CmsOffer } from '../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../types/pushDataLayer';
import { getUrgencyPhase } from '../../../utils/helpers/calculateRewardValue';
import { currencyFormat } from '../../../utils/helpers/formatters';
import { currencyFormatterNoDecimal } from '../../../utils/number';
import { fireRewardTileClick } from '../helpers/analytics';
import { StaticText } from '../i18n';
interface RewardTilesProps {
  exclusiveOffers: CmsOffer[];
  welcomeOffers: CmsOffer[];
  lang: string;
  staticText: StaticText;
  pushDataLayer: PushDataLayerFn;
  onClick?: () => void;
}

export const RewardTiles: React.FC<RewardTilesProps> = ({
  exclusiveOffers,
  welcomeOffers,
  lang,
  staticText,
  pushDataLayer,
  onClick,
}) => {
  const allOffers = [...exclusiveOffers, ...welcomeOffers];
  const gifts = allOffers.flatMap((offer) =>
    (offer.list_of_gifts ?? []).map((g) => ({
      ...g,
      offerType: offer.type,
      startDate: offer.period?.start_date,
      endDate: offer.period?.end_date,
    })),
  );

  if (gifts.length === 0) {
    return null;
  }

  const getThumbnailProps = (gift: (typeof gifts)[number]) => {
    const giftValue = gift.gift?.gift_value;
    const prefix = currencyFormat(giftValue?.prefix ?? '');
    const price = giftValue?.price
      ? currencyFormatterNoDecimal.format(giftValue.price)
      : '';
    const title = price
      ? `${staticText.worth} ${prefix}${price}`
      : (gift.gift?.gift_title ?? '');
    const subtitle =
      giftValue?.topup_amount && giftValue?.topup_label
        ? `${giftValue.topup_label} ${prefix}${currencyFormatterNoDecimal.format(giftValue.topup_amount)}`
        : '';
    const urgencyPhase = getUrgencyPhase(lang, gift.startDate, gift.endDate);
    const tagText = giftValue?.urgency_label || urgencyPhase?.value;

    return {
      image: gift.gift?.desktop_image ?? '',
      tooltip: gift.gift?.gift_title ?? '',
      title,
      subtitle,
      tagText,
      urgentTagText: !!giftValue?.urgency_label,
    };
  };

  const handleClick = (gift: (typeof gifts)[number]) => {
    if (!onClick) {
      return;
    }

    const giftValue = gift.gift?.gift_value;
    fireRewardTileClick(pushDataLayer, {
      source: gift.offerType === 'exclusive' ? 'singsaver' : 'provider',
      rewardId: gift.gift?.uid ?? '',
      name: gift.gift?.gift_title ?? '',
      value: giftValue?.price ?? 0,
    });

    onClick();
  };

  return (
    <>
      {/* Desktop grid — fluid width tiles */}
      <div className="hidden gap-2 lg:grid lg:grid-cols-3">
        {gifts.map((gift) => (
          <button
            key={gift.gift?.uid}
            type="button"
            className="text-left"
            onClick={onClick ? () => handleClick(gift) : undefined}
          >
            <RewardThumbnail
              data-gift-uid={gift.gift?.uid}
              {...getThumbnailProps(gift)}
              rewardThumbnailSizeClasses="h-36 max-w-[144px]"
            />
          </button>
        ))}
      </div>

      {/* Tablet/Mobile carousel — fixed width tiles */}
      <div className="w-full lg:hidden">
        <FeCarouselV2
          slidesPerView="auto"
          slideClassName="!h-auto flex flex-col"
          pagination={{ showOnMobile: true }}
        >
          {gifts.map((gift) => (
            <button
              key={gift.gift?.uid}
              type="button"
              className="text-left"
              onClick={onClick ? () => handleClick(gift) : undefined}
            >
              <RewardThumbnail
                data-gift-uid={gift.gift?.uid}
                {...getThumbnailProps(gift)}
                rewardThumbnailSizeClasses="h-32 w-[145px] md:h-36 md:w-41"
              />
            </button>
          ))}
        </FeCarouselV2>
      </div>
    </>
  );
};
